// Global formatting utilities — Single source of truth

/**
 * Format number compactly:
 * 1234 -> "1.2K"
 * 62126 -> "62K"
 * 1250000 -> "1.2M"
 */
export function fmtCompact(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1).replace(/\.0$/, "")}B`;
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace(/\.0$/, "")}K`;
  return String(Math.round(v));
}

/** Full localized number: 62126 -> "62,126" */
export function fmtFull(n) {
  return Number(n || 0).toLocaleString();
}

/** Money: $1,234.56 */
export function fmtMoney(n, fractionDigits = 2) {
  const v = Number(n || 0);
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`;
}

/** Compact money: $62K, $1.2M */
export function fmtMoneyCompact(n) {
  return `$${fmtCompact(n)}`;
}

/** Capitalize a single word: "active" -> "Active", "UNDER_APPEAL" -> "Under Appeal" */
export function fmtStatus(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Time-ago string: "Just now", "2m", "5h", "3d" */
export function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 45) return "Just now";
  if (diff < 60 * 60) return `${Math.round(diff / 60)}m`;
  if (diff < 60 * 60 * 24) return `${Math.round(diff / 3600)}h`;
  if (diff < 60 * 60 * 24 * 30) return `${Math.round(diff / 86400)}d`;
  if (diff < 60 * 60 * 24 * 365) return `${Math.round(diff / (86400 * 30))}mo`;
  return `${Math.round(diff / (86400 * 365))}y`;
}

/** Format a date as "Feb 12, 2026" */
export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** "Ends in 4 days" / "Ended" / "Starts in 2d" */
export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
}
